import { InfoOutlined } from "@mui/icons-material";
import { ClickAwayListener, IconButton, InputAdornment, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormatPrice } from "../hooks";

/**
 * The ⓘ at the end of a sale line's PRICE box. It says the lowest price this line may be
 * sold at, in the unit it is being sold in. Every sale-entry screen puts it in the same
 * place (POS cart, Direct Invoice / Sales Order grid), so a cashier learns it once.
 *
 * The figure is IN the tooltip, shown on hover and toggled by a tap (touch tills and
 * phones have no hover). It was a click popover first and showed nothing: the click
 * blurred the price box, whose blur handler re-prices the line and re-renders the row, and
 * the popover lost its anchor. The press now keeps focus where it is (preventDefault on
 * mousedown), so no blur, no re-price, nothing to lose.
 *
 * `minPrice` comes from utils/cartLine.js lineMinPrice, the same figure the box is checked
 * against, so what this says and what the box refuses cannot disagree.
 *
 * `rule` says whether the minimum is enforced:
 *   "enforced" — a lower price is refused (flat / dynamic prices, or open price where the
 *                branch blocks lower prices);
 *   "advisory" — open price at a branch that does not block: a lower price only warns;
 *   "branch"   — open price on a screen that does not know the branch's setting; the
 *                server applies it on save.
 */
const MinPriceInfo = ({ minPrice, unitLabel, rule = "enforced" }) => {
  const { t } = useTranslation("common");
  const formatPrice = useFormatPrice();
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const min = parseFloat(minPrice) || 0;

  const note =
    min <= 0
      ? null
      : rule === "advisory"
      ? t("minPriceAdviceOnly", "Advice only — this branch allows selling lower.")
      : rule === "branch"
      ? t("minPriceBranchRule", "Open price — applied only where the branch blocks lower prices.")
      : null;

  // textTransform reset: the app's tooltip theme uppercases, which suits a one-word label
  // and not a price with a sentence under it.
  const title = (
    <div style={{ textTransform: "none" }} className="py-[2px]">
      <div className="text-[11px] opacity-80">{t("minSalePrice", "Minimum sale price")}</div>
      {min > 0 ? (
        <div className="text-[13px] font-semibold whitespace-nowrap">
          {formatPrice(min)}
          {unitLabel && (
            <span className="font-normal opacity-80">
              {" "}
              {t("perUnit", { unit: unitLabel, defaultValue: "per {{unit}}" })}
            </span>
          )}
        </div>
      ) : (
        <div className="text-[12px]">{t("noMinPriceSet", "No minimum price set")}</div>
      )}
      {note && <div className="text-[11px] opacity-80 mt-[2px] max-w-[220px]">{note}</div>}
    </div>
  );

  return (
    <InputAdornment position="end" sx={{ ml: "2px", mr: 0 }}>
      <ClickAwayListener onClickAway={() => setPinned(false)}>
        <Tooltip
          title={title}
          arrow
          placement="top"
          open={hover || pinned}
          onOpen={() => setHover(true)}
          onClose={() => setHover(false)}
          disableFocusListener
        >
          <IconButton
            size="small"
            tabIndex={-1}
            aria-label={t("minSalePrice", "Minimum sale price")}
            // Keep the price box focused: a blur here would commit a half-typed price.
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              setPinned((p) => !p);
            }}
            sx={{ p: "2px", color: "#9ca3af", "&:hover": { color: "#2563eb" } }}
          >
            <InfoOutlined sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </ClickAwayListener>
    </InputAdornment>
  );
};

export default MinPriceInfo;
